import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PageHeader } from "@/components/shell/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { Repeat, Sparkles, Link as LinkIcon, FileText, Layers, Loader2, Mic } from "lucide-react";
import { useAppState } from "@/state/AppStateProvider";
import { sourcesApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { motion } from "framer-motion";

const OUTPUTS = [
  { id: "linkedin_post", label: "LinkedIn post", platform: "linkedin" },
  { id: "x_thread", label: "X thread", platform: "x" },
  { id: "shorts_script", label: "Short-form video script", platform: "youtube_shorts" },
  { id: "instagram_caption", label: "Instagram caption", platform: "instagram" },
  { id: "threads_post", label: "Threads post", platform: "threads" },
  { id: "facebook_post", label: "Facebook post", platform: "facebook" },
];

const REPURPOSE_PROMPT =
  "Repurpose the source material below into a platform-native post. Keep the key ideas, " +
  "facts, and any specifics from the source; adapt the length, structure, and hook to the " +
  "platform. Do not invent anything that isn't in the source.";

export default function Repurposer() {
  const [search] = useSearchParams();
  const { state, startAgentRun } = useAppState();
  const requestedOutput = search.get("output");
  const requestedBlogId = search.get("blog");
  const [tab, setTab] = useState("paste");
  const [input, setInput] = useState("");
  const [url, setUrl] = useState("");
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [selectedOutputs, setSelectedOutputs] = useState(() => (
    OUTPUTS.some((output) => output.id === requestedOutput) ? [requestedOutput] : ["linkedin_post", "x_thread", "shorts_script"]
  ));
  const [activeRunId, setActiveRunId] = useState(null);
  const [uploadedName, setUploadedName] = useState("");

  // Active brand + its primary voice drive the repurpose recipe (same voice
  // system as Create/Blog — no separate "tone" concept).
  const brands = useMemo(() => state.brands || [], [state.brands]);
  const activeBrand = useMemo(
    () => brands.find((b) => b.id === state.activeBrandId) || brands.find((b) => b.is_default) || brands[0] || null,
    [brands, state.activeBrandId],
  );
  const primaryVoice = useMemo(() => {
    const voices = activeBrand?.voices || [];
    return voices.find((v) => v.is_primary) || voices[0] || null;
  }, [activeBrand]);

  const activeRun = useMemo(() => (state.runs || []).find((r) => r.id === activeRunId) || null, [state.runs, activeRunId]);
  const runAssets = useMemo(() => {
    if (!activeRun?.asset_ids?.length) return [];
    // Only the real platform posts — drop supporting assets (image prompt / research brief).
    return (state.assets || []).filter(
      (a) => activeRun.asset_ids.includes(a.id) && !["image_prompt", "research_brief", "execution_report"].includes(a.asset_type),
    );
  }, [state.assets, activeRun]);
  const running = Boolean(activeRun && activeRun.status === "running");

  const toggleOut = (id) => setSelectedOutputs((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const onGenerate = () => {
    const src = input.trim();
    if (!src) return toast.error("Add source text first (paste, upload, a URL, or pick a blog)");
    const platforms = [...new Set(OUTPUTS.filter((o) => selectedOutputs.includes(o.id)).map((o) => o.platform))];
    if (!platforms.length) return toast.error("Pick at least one output");
    const first = (src.split("\n").find(Boolean) || "source").slice(0, 60);
    const id = startAgentRun({
      title: `Repurpose: ${first}`,
      prompt: REPURPOSE_PROMPT,
      platforms,
      context: src,
      source_label: "Repurposed source",
      brand_id: activeBrand?.id || undefined,
      voice_id: primaryVoice?.id || undefined,
      research_mode: "none",
      image_mode: "none",
    });
    setActiveRunId(id);
    toast.success("Repurposing with your connected model…");
  };

  const onLoadUrl = async () => {
    const u = url.trim();
    if (!u) return toast.error("Paste a URL first");
    setLoadingUrl(true);
    try {
      const r = await sourcesApi.extract(u);
      setInput(r.text || "");
      setTab("paste");
      toast.success(`Loaded ${r.title || u}${r.truncated ? " (trimmed for length)" : ""}`);
    } catch (e) {
      toast.error(`Couldn't fetch that URL: ${e.message}`);
    } finally {
      setLoadingUrl(false);
    }
  };

  const onUploadSource = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/\.(md|txt)$/i.test(file.name) && !file.type.startsWith("text/")) {
      event.target.value = "";
      return toast.error("Use a .md or .txt file for now");
    }
    const reader = new FileReader();
    reader.onload = () => {
      setInput(String(reader.result || ""));
      setUploadedName(file.name);
      setTab("paste");
      toast.success(`${file.name} loaded`);
    };
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsText(file);
    event.target.value = "";
  };

  const onPickBlog = (blog) => {
    setInput(plainTextFromHtml(blog.body_html || blog.excerpt || blog.title));
    setTab("paste");
    toast.success(`Loaded ${blog.title}`);
  };

  useEffect(() => {
    if (!requestedBlogId) return;
    const blog = (state.blogs || []).find((item) => item.id === requestedBlogId);
    if (!blog) return;
    onPickBlog(blog);
    if (OUTPUTS.some((output) => output.id === requestedOutput)) setSelectedOutputs([requestedOutput]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedBlogId, requestedOutput, state.blogs]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="repurpose-page">
      <PageHeader
        eyebrow="Create · Repurpose"
        title="Turn one idea into ten pieces"
        description="Paste a blog, URL, transcript, or notes. Pick the platforms. The agent rewrites it into platform-native posts, grounded only on your source, in your brand voice."
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <section className="space-y-4 xl:col-span-7">
          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-transparent p-0 gap-1">
                <TabsTrigger value="paste" className="" data-testid="repurpose-tab-paste"><FileText className="mr-1.5 h-3.5 w-3.5" /> Paste Text</TabsTrigger>
                <TabsTrigger value="url" className="" data-testid="repurpose-tab-url"><LinkIcon className="mr-1.5 h-3.5 w-3.5" /> URL</TabsTrigger>
                <TabsTrigger value="upload" className="" data-testid="repurpose-tab-upload">Upload</TabsTrigger>
                <TabsTrigger value="existing" className="" data-testid="repurpose-tab-existing">Existing blog</TabsTrigger>
              </TabsList>
              <TabsContent value="paste" className="mt-4">
                <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={10} placeholder="Paste a blog, transcript, or notes here…" className="font-mono text-[13px] leading-[1.65]" data-testid="repurpose-input-paste" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                {input.trim() && <p className="mt-1.5 text-[11px] text-muted-foreground">{input.trim().split(/\s+/).length.toLocaleString()} words of source loaded.</p>}
              </TabsContent>
              <TabsContent value="url" className="mt-4">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Source URL</Label>
                <div className="mt-2 flex gap-2">
                  <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onLoadUrl()} placeholder="https://adamdoesai.com/blog/…" data-testid="repurpose-input-url" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
                  <Button type="button" variant="outline" className="shrink-0 gap-1.5" onClick={onLoadUrl} disabled={loadingUrl} data-testid="repurpose-url-load">
                    {loadingUrl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />} Load
                  </Button>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">We fetch the page and extract its text into the source box.</p>
              </TabsContent>
              <TabsContent value="upload" className="mt-4">
                <div className="flex min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
                  <p>{uploadedName ? `Loaded ${uploadedName}` : "Upload a .md or .txt source file"}</p>
                  <input type="file" accept=".md,.txt,text/plain,text/markdown" onChange={onUploadSource} data-testid="repurpose-upload-input" />
                </div>
              </TabsContent>
              <TabsContent value="existing" className="mt-4">
                <div className="space-y-2" data-testid="repurpose-existing-list">
                  {(state.blogs || []).length === 0 && <p className="text-[12.5px] text-muted-foreground">No saved blogs yet — write one in Blog Studio first.</p>}
                  {(state.blogs || []).map((blog) => (
                    <button
                      key={blog.id}
                      type="button"
                      onClick={() => onPickBlog(blog)}
                      className="block w-full rounded-lg border px-3 py-2 text-left text-sm hover:bg-white/[0.04]"
                      style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}
                      data-testid={`repurpose-existing-${blog.id}`}
                    >
                      <span className="block font-medium text-1">{blog.title}</span>
                      <span className="text-[11.5px] text-muted-foreground">{blog.status} - {blog.word_count || 0} words</span>
                    </button>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Output selectors */}
          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Outputs</p>
                <h3 className="text-sm font-semibold text-1">Pick the platforms to produce</h3>
              </div>
              <span className="text-[11px] text-muted-foreground">{selectedOutputs.length} selected</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="repurpose-outputs">
              {OUTPUTS.map((o) => {
                const active = selectedOutputs.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggleOut(o.id)}
                    data-testid={`repurpose-output-${o.id}`}
                    className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors"
                    style={{
                      backgroundColor: active ? "rgba(18, 147, 79,0.08)" : "var(--surface-2)",
                      borderColor: active ? "rgba(18, 147, 79,0.28)" : "var(--border-subtle)",
                      color: active ? "#0B7C42" : "var(--text-2)",
                    }}
                  >
                    <PlatformBadge platform={o.platform} compact />
                    <span>{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Voice (from the active brand) */}
          <div className="flex items-center gap-2 rounded-xl border bg-card p-4 inner-highlight text-[12.5px]" style={{ borderColor: "var(--border-subtle)" }}>
            <Mic className="h-4 w-4 shrink-0 text-[color:var(--accent-cyan)]" />
            <span className="text-muted-foreground">
              Written in{" "}
              <span className="font-medium text-1">{activeBrand?.name || "your brand"}</span>
              {primaryVoice ? <> · <span className="font-medium text-1">{primaryVoice.name}</span> voice</> : " (no voice set)"}.
              {" "}<Link to="/brands" className="text-[color:var(--accent-cyan)] hover:underline">Change in Brands</Link>
            </span>
          </div>

          <div className="flex justify-end">
            <Button onClick={onGenerate} disabled={running} className="gap-1.5 px-5" data-testid="repurpose-generate" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {running ? "Repurposing…" : "Generate package"}
            </Button>
          </div>
        </section>

        {/* Preview package */}
        <aside className="space-y-4 xl:col-span-5" data-testid="repurpose-preview">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-1"><Repeat className="inline h-4 w-4 mr-1.5 text-[color:var(--accent-cyan)]" /> Generated package</h3>
            {activeRun && <StatusBadge status={running ? "draft" : "needs_review"} />}
          </div>

          {!activeRun ? (
            <div className="rounded-xl border border-dashed p-8 text-center" style={{ borderColor: "var(--border-subtle)" }}>
              <Layers className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-1">Nothing generated yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Load a source, pick platforms, and press <span className="font-medium">Generate package</span>.</p>
            </div>
          ) : running ? (
            <div className="rounded-xl border p-8 text-center" style={{ borderColor: "var(--border-subtle)" }}>
              <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-[color:var(--accent-cyan)]" />
              <p className="text-sm text-1">Repurposing your source…</p>
              <p className="mt-1 text-xs text-muted-foreground">Writing {(activeRun.platforms || []).length} platform-native cut{(activeRun.platforms || []).length === 1 ? "" : "s"}.</p>
            </div>
          ) : runAssets.length === 0 ? (
            <div className="rounded-xl border p-6 text-center" style={{ borderColor: "var(--border-subtle)" }}>
              <p className="text-sm text-1">No posts came back.</p>
              <p className="mt-1 text-xs text-muted-foreground">The model may have failed or returned empty — try again or check Settings.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="repurpose-results">
              {runAssets.map((a) => (
                <article key={a.id} className="rounded-xl border bg-card p-4 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid={`repurpose-result-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <PlatformBadge platform={a.platform} />
                    <StatusBadge status={a.status || "needs_review"} />
                  </div>
                  {a.hook && <h4 className="mt-2 text-sm font-semibold text-1">{a.hook}</h4>}
                  <p className="mt-1 text-sm text-foreground/85 line-clamp-4 leading-[1.6]">{a.body}</p>
                  {a.cta && a.cta !== "—" && <p className="mt-1.5 text-[12.5px] text-[color:var(--accent-cyan)]">{a.cta}</p>}
                  <div className="mt-2.5 flex justify-end">
                    <Link to={`/library/${a.id}`} className="text-[11.5px] font-semibold text-[color:var(--accent-cyan)] hover:underline" data-testid={`repurpose-edit-${a.id}`}>
                      Edit &amp; approve in Library →
                    </Link>
                  </div>
                </article>
              ))}
              <p className="text-[11.5px] text-muted-foreground">All cuts are saved as review-ready drafts in your Library.</p>
            </div>
          )}
        </aside>
      </div>
    </motion.div>
  );
}

function plainTextFromHtml(html = "") {
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
